import { useCallback } from 'react';
import { useSelectionStore } from '../store/selectionStore';

interface UseSelectionProps<T> {
  items: T[];
  getItemId: (item: T) => string | number;
}

interface UseSelectionReturn<T> {
  selected: string[];
  isSelected: (item: T) => boolean;
  toggleSelection: (item: T) => void;
  toggleAll: () => void;
  isAllSelected: boolean;
  isSomeSelected: boolean;
  selectedItems: T[];
  clearSelection: () => void;
}

export const useSelection = <T>({ items, getItemId }: UseSelectionProps<T>): UseSelectionReturn<T> => {
  const { selectedIds, setSelectedIds, clearSelection } = useSelectionStore();

  const isSelected = useCallback((item: T) => {
    const id = getItemId(item).toString();
    return selectedIds.includes(id);
  }, [selectedIds, getItemId]);

  const toggleSelection = useCallback((item: T) => {
    const id = getItemId(item).toString();
    setSelectedIds((current: string[]) => {
      const selectedIndex = current.indexOf(id);
      if (selectedIndex === -1) {
        return [...current, id];
      }
      return current.filter((selectedId: string) => selectedId !== id);
    });
  }, [getItemId, setSelectedIds]);

  const toggleAll = useCallback(() => {
    const currentPageIds = items.map(item => getItemId(item).toString());
    const allCurrentPageSelected = currentPageIds.every(id => selectedIds.includes(id));
    
    if (allCurrentPageSelected) {
      setSelectedIds(selectedIds.filter(id => !currentPageIds.includes(id)));
    } else {
      const newSelections = [...selectedIds];
      currentPageIds.forEach(id => {
        if (!newSelections.includes(id)) {
          newSelections.push(id);
        }
      });
      setSelectedIds(newSelections);
    }
  }, [items, getItemId, selectedIds, setSelectedIds]);

  const selectedItems = items.filter(item => isSelected(item));
  const isAllSelected = items.length > 0 && selectedItems.length === items.length;
  const isSomeSelected = selectedItems.length > 0 && selectedItems.length < items.length;

  return {
    selected: selectedIds,
    isSelected,
    toggleSelection,
    toggleAll,
    isAllSelected,
    isSomeSelected,
    selectedItems,
    clearSelection
  };
}; 